import { useQuery } from '@tanstack/react-query';

// API endpoint for taskbloom-nest Supabase Edge Function
// Edge Function uses service role key to bypass RLS policies
const SUPABASE_URL = import.meta.env.VITE_TASKBLOOM_SUPABASE_URL || 'https://mxtkabejdsoqkgtqjaaw.supabase.co';
const FUNCTION_NAME = 'hr-dashboard';

export interface HRMember {
  id: string;
  user_id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  position: string | null;
  manager_id: string | null;
  is_team_leader: boolean;
  can_view_tasks: boolean;
  can_create_tasks: boolean;
  can_edit_tasks: boolean;
  can_delete_tasks: boolean;
  can_manage_members: boolean;
  can_manage_job_descriptions: boolean;
  is_active: boolean;
  joined_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface HRTask {
  id: string;
  title: string;
  description: string | null;
  status: 'todo' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'urgent' | null;
  assigned_to: string | null;
  created_by: string | null;
  due_date: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  started_at?: string | null;
  time_spent_hours?: number | null;
  time_spent_days?: number | null;
}

interface HRDashboardData {
  members: HRMember[];
  tasks: HRTask[];
}

export const useHRDashboardData = () => {
  return useQuery<HRDashboardData, Error>({
    queryKey: ['hrDashboard'],
    queryFn: async () => {
      try {
        // Add timestamp to bust cache
        const timestamp = Date.now();
        const url = `${SUPABASE_URL}/functions/v1/${FUNCTION_NAME}?t=${timestamp}`;
        console.log('Fetching HR dashboard from Edge Function:', url);
        
        // Get anon key for taskbloom Supabase
        const anonKey = import.meta.env.VITE_TASKBLOOM_SUPABASE_ANON_KEY || 
          'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im14dGthYmVqZHNvcWtndHFqYWF3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU1MTIxMzIsImV4cCI6MjA3MTA4ODEzMn0.RXQ_l31IZwdLZngsixdalK8LRrkZJVEIPdfJFMgtKhg';
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
            'apikey': anonKey,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }

        const data = await response.json();
        console.log('Fetched HR dashboard data from Edge Function:', data);
        
        return data;
      } catch (error) {
        console.error('Error fetching HR dashboard data:', error);
        throw error;
      }
    },
    staleTime: 0, // No cache - always fetch fresh data
    refetchOnWindowFocus: true, // Auto-refetch when user returns to tab
    retry: 2,
  });
};

